/**
 * Runtime cache to prevent duplicate fetches per user session
 * Ensures profile, shortlists, and saved_reports are only fetched once per userId
 */

interface CacheState {
    userId: string | null;
    loaded: {
        profile: boolean;
        shortlists: boolean;
        savedReports: boolean;
    };
    inFlight: {
        profile: boolean;
        shortlists: boolean;
        savedReports: boolean;
    };
    renderCount: number; // Debug: track render count
}

const cache: CacheState = {
    userId: null,
    loaded: {
        profile: false,
        shortlists: false,
        savedReports: false,
    },
    inFlight: {
        profile: false,
        shortlists: false,
        savedReports: false,
    },
    renderCount: 0,
};

export const runtimeCache = {
    // Reset cache when userId changes
    reset: (newUserId: string | null) => {
        if (cache.userId !== newUserId) {
            if (import.meta.env.DEV) {
                console.log('[runtimeCache] Resetting cache:', { oldUserId: cache.userId, newUserId });
            }
            cache.userId = newUserId;
            cache.loaded = {
                profile: false,
                shortlists: false,
                savedReports: false,
            };
            cache.inFlight = {
                profile: false,
                shortlists: false,
                savedReports: false,
            };
            cache.renderCount = 0;
        }
    },

    // Check if a resource should be fetched (not loaded, not in flight, userId matches)
    shouldFetch: (resource: 'profile' | 'shortlists' | 'savedReports', userId: string | null): boolean => {
        if (!userId) return false;
        if (cache.userId !== userId) return true; // Different user, allow fetch
        if (cache.loaded[resource]) return false; // Already loaded
        if (cache.inFlight[resource]) return false; // Already fetching
        return true;
    },

    // Mark a resource as in-flight
    markInFlight: (resource: 'profile' | 'shortlists' | 'savedReports', userId: string | null) => {
        if (!userId) return;
        if (cache.userId !== userId) {
            runtimeCache.reset(userId);
        }
        cache.inFlight[resource] = true;
        if (import.meta.env.DEV) {
            console.log(`[runtimeCache] Marking ${resource} in-flight for user ${userId}`);
        }
    },

    // Mark a resource as loaded
    markLoaded: (resource: 'profile' | 'shortlists' | 'savedReports', userId: string | null) => {
        if (!userId) return;
        if (cache.userId !== userId) {
            runtimeCache.reset(userId);
        }
        cache.loaded[resource] = true;
        cache.inFlight[resource] = false;
        if (import.meta.env.DEV) {
            console.log(`[runtimeCache] Marking ${resource} loaded for user ${userId}`);
        }
    },

    // Mark a resource fetch as failed (allow retry later if needed)
    markFailed: (resource: 'profile' | 'shortlists' | 'savedReports', userId: string | null) => {
        if (!userId) return;
        if (cache.userId !== userId) {
            runtimeCache.reset(userId);
        }
        cache.inFlight[resource] = false;
        // Don't mark as loaded on failure - allows retry if explicitly requested
        if (import.meta.env.DEV) {
            console.log(`[runtimeCache] Marking ${resource} fetch failed for user ${userId}`);
        }
    },

    // Check if a resource is loaded
    isLoaded: (resource: 'profile' | 'shortlists' | 'savedReports', userId: string | null): boolean => {
        if (!userId || cache.userId !== userId) return false;
        return cache.loaded[resource];
    },

    // Force reset a specific resource (e.g., after update)
    resetResource: (resource: 'profile' | 'shortlists' | 'savedReports', userId: string | null) => {
        if (!userId) return;
        if (cache.userId !== userId) {
            runtimeCache.reset(userId);
        }
        cache.loaded[resource] = false;
        cache.inFlight[resource] = false;
        if (import.meta.env.DEV) {
            console.log(`[runtimeCache] Resetting ${resource} for user ${userId}`);
        }
    },

    // Increment render count (for debug)
    incrementRender: () => {
        cache.renderCount++;
    },

    // Get current cache state (for debug)
    getState: (): CacheState => {
        return { ...cache };
    },
};
